import {Button, Dropdown, Space} from 'antd'
import {MoreOutlined} from '@ant-design/icons'

/**
 * TableActions - Standardized action buttons for table rows.
 *
 * Rules:
 *  - The "edit" action (key === 'edit') is always rendered as a visible Icon+Text button.
 *  - ALL other actions (delete, extras) are placed in a MoreOutlined dropdown.
 *  - If there is no "edit" action, all actions are rendered inline (e.g. Delete-only tables).
 *  - If there is only an "edit" action, no dropdown is shown.
 *
 * Usage:
 *   <TableActions
 *     actions={[
 *       {key: 'edit', label: 'Edit', icon: <EditOutlined />, onClick: () => onEdit(record)},
 *       {key: 'delete', label: 'Delete', icon: <DeleteOutlined />, danger: true, onClick: () => onDelete(record)},
 *       {key: 'clone', label: 'Clone SVG', icon: <CopyOutlined />, onClick: () => onClone(record)},
 *     ]}
 *   />
 */
const TableActions = ({actions}) => {
  if (!actions?.length) return null

  const editAction = actions.find((a) => a.key === 'edit')
  const otherActions = actions.filter((a) => a.key !== 'edit')

  // If no Edit action exists, render all actions inline (no dropdown)
  if (!editAction) {
    return (
      <Space size="small">
        {actions.map((action) => (
          <Button
            key={action.key}
            type="link"
            className={`link-button${action.danger ? ' danger' : ''}`}
            icon={action.icon}
            danger={action.danger}
            onClick={action.onClick}
          >
            {action.label}
          </Button>
        ))}
      </Space>
    )
  }

  const dropdownItems = otherActions.map((item) => ({
    key: item.key,
    label: item.label,
    icon: item.icon,
    danger: item.danger,
    onClick: item.onClick,
  }))

  return (
    <Space size="small">
      <Button
        type="link"
        className="link-button"
        icon={editAction.icon}
        onClick={editAction.onClick}
      >
        {editAction.label}
      </Button>
      {dropdownItems.length > 0 && (
        <Dropdown menu={{items: dropdownItems}} trigger={['click']} placement="bottomRight">
          <Button icon={<MoreOutlined />} />
        </Dropdown>
      )}
    </Space>
  )
}

export default TableActions
